<template>
  <div class="audio-attachment">
    <div class="audio-attachment__card">
      <div class="audio-attachment__icon-wrapper">
        <Icon icon="mdi:music-note" class="audio-attachment__icon" />
      </div>
      <button
        v-if="audioLoaded && resolvedAudioUrl"
        class="audio-attachment__play"
        @click.stop="togglePlay"
        title="Play/Pause"
      >
        <Icon v-if="isPlaying" icon="mdi:pause" class="audio-attachment__play-icon" />
        <Icon v-else icon="mdi:play" class="audio-attachment__play-icon" />
      </button>
      <div v-else class="audio-attachment__preview-btn" @click="loadAudio">
        <Icon icon="majesticons:arrow-down" />
      </div>
      <div class="audio-attachment__content">
        <div class="audio-attachment__info">
          <span class="audio-attachment__name">{{ attachment.name || "Audio" }}</span>
          <span v-if="attachment.duration" class="audio-attachment__duration">
            {{ formatDuration(attachment.duration) }}
          </span>
          <span
            v-if="
              attachment.file_size ||
              (typeof attachment.size === 'number' ? attachment.size : undefined)
            "
            class="audio-attachment__size"
          >
            {{
              formatFileSize(
                attachment.file_size ||
                  (typeof attachment.size === "number" ? attachment.size : undefined)
              )
            }}
          </span>
        </div>
      </div>
      <button
        v-if="audioLoaded"
        class="audio-attachment__download"
        @click.stop="handleDownload"
        :disabled="isDownloading"
        title="Download audio"
      >
        <ProgressCircle
          v-if="isDownloading && downloadProgress > 0"
          :progress="downloadProgress"
          size="small"
          bg-stroke="rgba(0, 0, 0, 0.1)"
          fill-stroke="#111827"
          :show-text="false"
        />
        <Icon v-else-if="isDownloading" icon="mdi:loading" class="audio-attachment__spinner" />
        <Icon v-else icon="mdi:download" />
      </button>
      <audio
        v-if="audioLoaded && resolvedAudioUrl"
        ref="audioRef"
        :src="resolvedAudioUrl"
        class="audio-attachment__player"
        @loadstart="handleLoadStart"
        @play="isPlaying = true"
        @pause="isPlaying = false"
        @ended="isPlaying = false"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import type { Attachment } from "@/dtos/Unipile.dto";
import { Icon } from "@iconify/vue";
import { useAttachmentLoader } from "@/composables/attachments/useAttachmentLoader";
import { useAttachmentDownload } from "@/composables/attachments/useAttachmentDownload";
import ProgressCircle from "./ProgressCircle.vue";

interface Props {
  attachment: Attachment;
  messageId?: string;
}

const props = defineProps<Props>();

const audioRef = ref<HTMLAudioElement | null>(null);
const isPlaying = ref(false);
const audioLoaded = ref(false);

const { isLoading, loadingProgress, blobUrl, loadAttachment, getAttachmentUrl } =
  useAttachmentLoader(props.attachment, props.messageId, () => {});

const {
  isDownloading,
  loadingProgress: downloadProgress,
  download,
} = useAttachmentDownload(props.attachment, props.messageId);

const resolvedAudioUrl = computed(() => {
  if (blobUrl.value) return blobUrl.value;
  return getAttachmentUrl(props.attachment.url);
});

const loadAudio = async () => {
  const blob = await loadAttachment("audio/mpeg");
  if (blob) {
    audioLoaded.value = true;
  }
};

const handleLoadStart = async () => {
  if (
    props.attachment.url?.startsWith("att://") &&
    !blobUrl.value &&
    props.messageId &&
    props.attachment.id
  ) {
    const blob = await loadAttachment("audio/mpeg");
    if (blob && audioRef.value) {
      audioRef.value.src = blobUrl.value!;
    }
  }
};

const togglePlay = async () => {
  if (!audioRef.value) return;

  if (
    props.attachment.url?.startsWith("att://") &&
    !blobUrl.value &&
    props.messageId &&
    props.attachment.id
  ) {
    const blob = await loadAttachment("audio/mpeg");
    if (blob && audioRef.value) {
      audioRef.value.src = blobUrl.value!;
      audioLoaded.value = true;
    }
  }

  if (audioRef.value.paused) {
    await audioRef.value.play();
  } else {
    audioRef.value.pause();
  }
};

const handleDownload = () => {
  download("audio/mpeg", `audio_${props.attachment.id}`);
};

watch(
  () => resolvedAudioUrl.value,
  (url) => {
    if (url && !props.attachment.url?.startsWith("att://")) {
      audioLoaded.value = true;
    }
  },
  { immediate: true }
);

const formatDuration = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  }
  return `${minutes}:${secs.toString().padStart(2, "0")}`;
};

const formatFileSize = (bytes?: number): string => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};
</script>

<style scoped>
.audio-attachment {
  position: relative;
  max-width: 300px;
}

.audio-attachment__card {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem;
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
}

.audio-attachment__card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-color: #d1d5db;
}

.audio-attachment__icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: #f3f4f6;
  border-radius: 10px;
  flex-shrink: 0;
}

.audio-attachment__icon {
  font-size: 1.25rem;
  color: #4b5563;
}

.audio-attachment__content {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
  min-width: 0;
}

.audio-attachment__info {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.audio-attachment__name {
  font-size: 0.875rem;
  font-weight: 600;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.audio-attachment__duration {
  font-size: 0.75rem;
  color: #6b7280;
}

.audio-attachment__size {
  font-size: 0.75rem;
  color: #6b7280;
}

.audio-attachment__player {
  position: absolute;
  opacity: 0;
  pointer-events: none;
  width: 0;
  height: 0;
}

.audio-attachment__play {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  background: #f3f4f6;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.audio-attachment__play:hover:not(:disabled) {
  background: #e5e7eb;
  transform: scale(1.05);
}

.audio-attachment__play:active:not(:disabled) {
  transform: scale(0.95);
}

.audio-attachment__play-icon {
  font-size: 1.25rem;
  color: #111827;
}

.audio-attachment__preview-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  background: #f3f4f6;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  color: #6b7280;
}

.audio-attachment__preview-btn:hover {
  background: #e5e7eb;
  color: #111827;
  transform: scale(1.05);
}

.audio-attachment__preview-btn:active {
  transform: scale(0.95);
}

.audio-attachment__preview-btn svg {
  font-size: 1.25rem;
}

.audio-attachment__download {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  color: #6b7280;
  background: #f3f4f6;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  position: relative;
}

.audio-attachment__download:hover:not(:disabled) {
  background: #e5e7eb;
  color: #111827;
  transform: scale(1.05);
}

.audio-attachment__download:active:not(:disabled) {
  transform: scale(0.95);
}

.audio-attachment__download:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

.audio-attachment__spinner {
  font-size: 1.25rem;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
